/**
 * G2eGente - Resultados da Pesquisa de Clima
 * Dashboard com gráficos e análises
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { climateSurveyAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { 
  ArrowLeft,
  BarChart3,
  Users,
  Percent,
  ThermometerSun,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  MessageSquare,
  Building,
  AlertCircle,
  Download,
  FileText,
  GitCompare,
  Check,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend
} from 'recharts';

const COLORS = ['#06b6d4', '#22c55e', '#eab308', '#f97316', '#ef4444', '#8b5cf6', '#ec4899'];
const SCALE_LABELS = {
  '1': 'Discordo totalmente',
  '2': 'Discordo',
  '3': 'Neutro',
  '4': 'Concordo',
  '5': 'Concordo totalmente'
};

export default function ClimateSurveyResults() {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [availableComparisons, setAvailableComparisons] = useState([]);
  const [selectedComparisons, setSelectedComparisons] = useState([]);
  const [showComparisonSelector, setShowComparisonSelector] = useState(false);

  useEffect(() => {
    fetchResults();
    fetchAvailableComparisons();
  }, [surveyId]);

  const fetchResults = async () => {
    try {
      const { data } = await climateSurveyAPI.getResults(surveyId);
      setResults(data);
    } catch (error) {
      toast.error('Erro ao carregar resultados');
      navigate('/company/climate-surveys');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAvailableComparisons = async () => {
    try {
      const { data } = await climateSurveyAPI.getAvailableComparisons(surveyId);
      setAvailableComparisons(data);
    } catch (error) {
      console.log('Nenhuma pesquisa disponível para comparação');
    }
  };
  
  const toggleComparison = (surveyId) => {
    setSelectedComparisons(prev => 
      prev.includes(surveyId) 
        ? prev.filter(id => id !== surveyId)
        : [...prev, surveyId]
    );
  };
  
  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    try {
      toast.info('Gerando relatório PDF...');
      
      // Buscar dados completos do relatório
      const { data: reportData } = await climateSurveyAPI.getReportData(surveyId);
      
      // Buscar dados de comparação se houver seleção
      let comparisonData = null;
      if (selectedComparisons.length > 0) {
        try {
          const { data } = await climateSurveyAPI.getComparison(surveyId, selectedComparisons);
          comparisonData = data;
        } catch (e) {
          console.warn('Erro ao buscar comparação:', e);
        }
      }
      
      // Importar jsPDF dinamicamente
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;
      
      // Cores do tema
      const primaryColor = [6, 182, 212]; // cyan-500
      const successColor = [34, 197, 94]; // green-500
      const warningColor = [234, 179, 8]; // yellow-500
      const dangerColor = [239, 68, 68]; // red-500
      
      // Função auxiliar para desenhar barra horizontal
      const drawHorizontalBar = (x, y, width, height, value, maxValue, color) => {
        // Fundo cinza
        doc.setFillColor(229, 231, 235);
        doc.roundedRect(x, y, width, height, 1, 1, 'F');
        // Barra preenchida
        const fillWidth = (value / maxValue) * width;
        if (fillWidth > 0) {
          doc.setFillColor(...color);
          doc.roundedRect(x, y, Math.max(fillWidth, 2), height, 1, 1, 'F');
        }
      };
      
      // Função auxiliar para verificar e adicionar nova página
      const checkNewPage = (currentY, neededHeight = 30) => {
        if (currentY + neededHeight > pageHeight - 20) {
          doc.addPage();
          return 20;
        }
        return currentY;
      };
      
      // Função auxiliar para desenhar cabeçalho de página
      const drawPageHeader = (title) => {
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 12, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(title, pageWidth / 2, 8, { align: 'center' });
      };
      
      // ==================== CAPA ====================
      let y = 40;
      
      // Logo da empresa (se disponível)
      if (reportData.empresa.logo_url) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = reportData.empresa.logo_url;
          });
          doc.addImage(img, 'PNG', (pageWidth - 40) / 2, y, 40, 30);
          y += 40;
        } catch (e) {
          console.warn('Não foi possível carregar o logo');
        }
      }
      
      // Título principal
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('RELATÓRIO DE CLIMA', pageWidth / 2, y, { align: 'center' });
      y += 12;
      
      doc.setFontSize(22);
      doc.setTextColor(...primaryColor);
      doc.text('ORGANIZACIONAL', pageWidth / 2, y, { align: 'center' });
      y += 25;
      
      // Nome da empresa
      doc.setFontSize(16);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(reportData.empresa.nome || 'Empresa', pageWidth / 2, y, { align: 'center' });
      y += 30;
      
      // Título da pesquisa
      doc.setFontSize(14);
      doc.setTextColor(30, 41, 59);
      doc.text(`"${reportData.pesquisa.titulo}"`, pageWidth / 2, y, { align: 'center' });
      y += 40;
      
      // Período
      doc.setFontSize(11);
      doc.setTextColor(100, 116, 139);
      const dataInicio = reportData.pesquisa.data_inicio ? new Date(reportData.pesquisa.data_inicio).toLocaleDateString('pt-BR') : 'N/A';
      const dataFim = reportData.pesquisa.data_fim ? new Date(reportData.pesquisa.data_fim).toLocaleDateString('pt-BR') : 'Em andamento';
      doc.text(`Período: ${dataInicio} a ${dataFim}`, pageWidth / 2, y, { align: 'center' });
      y += 8;
      doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, pageWidth / 2, y, { align: 'center' });
      
      // Indicar se é comparativo
      if (comparisonData) {
        y += 15;
        doc.setFontSize(10);
        doc.setTextColor(...primaryColor);
        doc.text(`Relatório Comparativo com ${comparisonData.pesquisas_comparadas.length} pesquisa(s) anterior(es)`, pageWidth / 2, y, { align: 'center' });
      }
      
      // Rodapé da capa
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      doc.text('CONFIDENCIAL - USO INTERNO', pageWidth / 2, pageHeight - 20, { align: 'center' });
      
      // ==================== PÁGINA 2: RESUMO EXECUTIVO ====================
      doc.addPage();
      drawPageHeader('RESUMO EXECUTIVO');
      y = 25;
      
      // KPIs principais em cards
      const kpis = [
        { label: 'Índice Geral de Satisfação', value: `${reportData.metricas.indice_geral}%`, color: reportData.metricas.indice_geral >= 60 ? successColor : dangerColor },
        { label: 'Taxa de Participação', value: `${reportData.metricas.taxa_participacao}%`, color: reportData.metricas.taxa_participacao >= 50 ? successColor : warningColor },
        { label: 'Total de Respostas', value: `${reportData.metricas.total_respostas}/${reportData.metricas.total_elegivel}`, color: primaryColor },
        { label: 'Categorias Avaliadas', value: `${reportData.metricas.total_categorias}`, color: primaryColor }
      ];
      
      const cardWidth = (pageWidth - 2 * margin - 10) / 2;
      const cardHeight = 25;
      
      kpis.forEach((kpi, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = margin + col * (cardWidth + 10);
        const cardY = y + row * (cardHeight + 5);
        
        doc.setFillColor(245, 245, 245);
        doc.roundedRect(x, cardY, cardWidth, cardHeight, 3, 3, 'F');
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(kpi.label, x + 5, cardY + 8);
        
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...kpi.color);
        doc.text(kpi.value, x + 5, cardY + 20);
      });
      
      y += (cardHeight + 5) * 2 + 15;
      
      // Termômetro do clima
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Termômetro do Clima Organizacional', margin, y);
      y += 8;
      
      const barWidth = pageWidth - 2 * margin;
      const barHeight = 12;
      
      // Fundo do termômetro (gradiente visual)
      const segments = [
        { pct: 0.25, color: [239, 68, 68] },
        { pct: 0.25, color: [234, 179, 8] },
        { pct: 0.25, color: [34, 197, 94] },
        { pct: 0.25, color: [6, 182, 212] }
      ];
      
      let segX = margin;
      segments.forEach(seg => {
        const segWidth = barWidth * seg.pct;
        doc.setFillColor(...seg.color);
        doc.rect(segX, y, segWidth, barHeight, 'F');
        segX += segWidth;
      });
      
      // Indicador de posição
      const indicatorX = margin + (barWidth * (reportData.metricas.indice_geral / 100));
      doc.setFillColor(30, 41, 59);
      doc.triangle(indicatorX - 4, y - 2, indicatorX + 4, y - 2, indicatorX, y + 2, 'F');
      
      y += barHeight + 5;
      
      // Legendas do termômetro
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text('Crítico (0-25%)', margin, y);
      doc.text('Atenção (25-50%)', margin + barWidth * 0.25, y);
      doc.text('Bom (50-75%)', margin + barWidth * 0.5, y);
      doc.text('Excelente (75-100%)', margin + barWidth * 0.75, y);
      
      y += 15;
      
      // Insights
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Principais Insights', margin, y);
      y += 8;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      
      reportData.insights.forEach((insight) => {
        const lines = doc.splitTextToSize(`• ${insight}`, pageWidth - 2 * margin);
        lines.forEach(line => {
          doc.text(line, margin, y);
          y += 5;
        });
        y += 2;
      });
      
      y += 10;
      
      // Recomendações
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Recomendações', margin, y);
      y += 8;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      reportData.recomendacoes.forEach((rec, i) => {
        doc.setTextColor(...primaryColor);
        doc.text(`${i + 1}.`, margin, y);
        doc.setTextColor(71, 85, 105);
        const lines = doc.splitTextToSize(rec, pageWidth - 2 * margin - 8);
        lines.forEach((line, j) => {
          doc.text(line, margin + 6, y + (j * 5));
        });
        y += lines.length * 5 + 3;
      });
      
      // ==================== PÁGINA 3: VISÃO GERAL - GRÁFICO RADAR ====================
      doc.addPage();
      drawPageHeader('VISÃO GERAL - MÉDIAS POR PERGUNTA');
      y = 25;
      
      // Preparar dados para o gráfico radar
      const radarDataPDF = reportData.perguntas_analise
        .filter(p => (p.tipo === 'escala_1_5' || p.tipo === 'escala_1_10') && p.media)
        .map((p, i) => ({
          label: `P${i + 1}`,
          value: p.media,
          maxValue: p.max_escala || 5,
          texto: p.texto
        }));
      
      if (radarDataPDF.length >= 3) {
        // Desenhar gráfico radar
        const radarCenterX = pageWidth / 2;
        const radarCenterY = y + 60;
        const radarRadius = 45;
        const numPoints = radarDataPDF.length;
        
        // Desenhar grid circular
        doc.setDrawColor(229, 231, 235); // gray-200
        doc.setLineWidth(0.3);
        
        // Círculos concêntricos (níveis do radar)
        for (let level = 1; level <= 5; level++) {
          const levelRadius = (radarRadius / 5) * level;
          // Desenhar polígono para cada nível
          const levelPoints = [];
          for (let i = 0; i < numPoints; i++) {
            const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
            levelPoints.push({
              x: radarCenterX + Math.cos(angle) * levelRadius,
              y: radarCenterY + Math.sin(angle) * levelRadius
            });
          }
          
          // Desenhar linhas do polígono do grid
          for (let i = 0; i < levelPoints.length; i++) {
            const nextI = (i + 1) % levelPoints.length;
            doc.line(levelPoints[i].x, levelPoints[i].y, levelPoints[nextI].x, levelPoints[nextI].y);
          }
        }
        
        // Desenhar eixos (linhas do centro para cada vértice)
        for (let i = 0; i < numPoints; i++) {
          const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
          const endX = radarCenterX + Math.cos(angle) * radarRadius;
          const endY = radarCenterY + Math.sin(angle) * radarRadius;
          doc.line(radarCenterX, radarCenterY, endX, endY);
          
          // Labels das perguntas
          const labelRadius = radarRadius + 8;
          const labelX = radarCenterX + Math.cos(angle) * labelRadius;
          const labelY = radarCenterY + Math.sin(angle) * labelRadius;
          
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 41, 59);
          doc.text(radarDataPDF[i].label, labelX, labelY + 3, { align: 'center' });
        }
        
        // Desenhar polígono dos dados (área preenchida)
        const dataPoints = radarDataPDF.map((d, i) => {
          const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
          const valueRadius = (d.value / d.maxValue) * radarRadius;
          return {
            x: radarCenterX + Math.cos(angle) * valueRadius,
            y: radarCenterY + Math.sin(angle) * valueRadius
          };
        });
        
        // Preencher área do radar com cor cyan clara
        doc.setFillColor(178, 235, 242); // cyan-200 (mais claro para simular transparência)
        doc.setDrawColor(6, 182, 212);
        doc.setLineWidth(2);
        
        // Criar path do polígono
        if (dataPoints.length > 0) {
          // Desenhar polígono preenchido manualmente usando triângulos
          for (let i = 0; i < dataPoints.length; i++) {
            const nextI = (i + 1) % dataPoints.length;
            doc.triangle(
              radarCenterX, radarCenterY,
              dataPoints[i].x, dataPoints[i].y,
              dataPoints[nextI].x, dataPoints[nextI].y,
              'F'
            );
          }
          
          // Desenhar contorno do polígono
          for (let i = 0; i < dataPoints.length; i++) {
            const nextI = (i + 1) % dataPoints.length;
            doc.line(dataPoints[i].x, dataPoints[i].y, dataPoints[nextI].x, dataPoints[nextI].y);
          }
          
          // Desenhar pontos nos vértices
          doc.setFillColor(6, 182, 212);
          dataPoints.forEach(point => {
            doc.circle(point.x, point.y, 1.5, 'F');
          });
        }
        
        y = radarCenterY + radarRadius + 20;
        
        // Legenda das perguntas
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        
        radarDataPDF.forEach((item, i) => {
          if (y > pageHeight - 25) {
            doc.addPage();
            drawPageHeader('VISÃO GERAL - MÉDIAS POR PERGUNTA (cont.)');
            y = 25;
          }
          
          const textoTruncado = item.texto.length > 70 ? item.texto.substring(0, 67) + '...' : item.texto;
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(6, 182, 212);
          doc.text(`${item.label}:`, margin, y);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(71, 85, 105);
          doc.text(textoTruncado, margin + 12, y);
          
          // Média
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 41, 59);
          doc.text(`(${item.value.toFixed(1)}/${item.maxValue})`, contentWidth - 5, y);
          
          y += 6;
        });
      }
      
      // ==================== PÁGINA: ÍNDICES POR CATEGORIA ====================
      doc.addPage();
      drawPageHeader('ÍNDICES POR CATEGORIA');
      y = 25;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Média de Satisfação por Categoria', margin, y);
      y += 10;
      
      // Gráfico de barras horizontais por categoria
      const barChartHeight = 12;
      const barChartGap = 8;
      const labelWidth = 60;
      const chartBarWidth = contentWidth - labelWidth - 25;
      
      reportData.analise_categorias.forEach((cat) => {
        y = checkNewPage(y, barChartHeight + barChartGap);
        
        // Nome da categoria
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);
        const catName = cat.categoria.length > 20 ? cat.categoria.substring(0, 18) + '...' : cat.categoria;
        doc.text(catName, margin, y + 7);
        
        // Barra
        let barColor = successColor;
        if (cat.status === 'critico') barColor = dangerColor;
        else if (cat.status === 'atencao') barColor = warningColor;
        
        drawHorizontalBar(margin + labelWidth, y, chartBarWidth, barChartHeight, cat.media_percentual, 100, barColor);
        
        // Valor
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...barColor);
        doc.text(`${cat.media_percentual}%`, margin + labelWidth + chartBarWidth + 3, y + 7);
        
        y += barChartHeight + barChartGap;
      });
      
      // ==================== PÁGINA 4: ANÁLISE DETALHADA POR PERGUNTA COM GRÁFICOS ====================
      doc.addPage();
      drawPageHeader('ANÁLISE DETALHADA POR PERGUNTA');
      y = 25;
      
      reportData.perguntas_analise.forEach((analise, index) => {
        // Calcular altura necessária para esta pergunta
        let neededHeight = 35;
        if (analise.tipo === 'escala_1_5') neededHeight = 70;
        else if (analise.tipo === 'escala_1_10') neededHeight = 90;
        else if (analise.tipo === 'sim_nao' || analise.tipo === 'multipla_escolha') neededHeight = 50;
        
        y = checkNewPage(y, neededHeight);
        
        if (y === 20) {
          drawPageHeader('ANÁLISE DETALHADA POR PERGUNTA (cont.)');
          y = 25;
        }
        
        // Card da pergunta
        doc.setFillColor(249, 250, 251);
        doc.roundedRect(margin, y, contentWidth, neededHeight - 5, 3, 3, 'F');
        
        // Número e tipo
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text(`P${index + 1}`, margin + 3, y + 6);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        const tipoLabel = analise.tipo === 'escala_1_5' ? 'Escala 1-5' : 
                         analise.tipo === 'escala_1_10' ? 'Escala 1-10' :
                         analise.tipo === 'sim_nao' ? 'Sim/Não' :
                         analise.tipo === 'texto' ? 'Texto' : 'Múltipla';
        doc.text(`[${tipoLabel}] ${analise.total_respostas} respostas`, margin + 12, y + 6);
        
        // Categoria
        if (analise.categoria) {
          doc.setTextColor(148, 163, 184);
          doc.text(`| ${analise.categoria}`, margin + 70, y + 6);
        }
        
        // Texto da pergunta
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);
        const perguntaLines = doc.splitTextToSize(analise.texto, contentWidth - 35);
        perguntaLines.slice(0, 2).forEach((line, i) => {
          doc.text(line, margin + 3, y + 13 + (i * 4));
        });
        
        // Média (se escala)
        if (analise.media) {
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          const mediaColor = analise.percentual >= 80 ? successColor : 
                            analise.percentual >= 60 ? warningColor : dangerColor;
          doc.setTextColor(...mediaColor);
          doc.text(`${analise.media.toFixed(1)}`, contentWidth - 5, y + 10);
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text(`de ${analise.max_escala || 5}`, contentWidth - 5, y + 15);
        }
        
        // Gráfico de distribuição
        if ((analise.tipo === 'escala_1_5' || analise.tipo === 'escala_1_10') && analise.distribuicao) {
          const chartY = y + 22;
          const chartBarH = 8;
          const chartBarW = 100;
          const maxVal = Math.max(...Object.values(analise.distribuicao), 1);
          const maxEscala = analise.tipo === 'escala_1_5' ? 5 : 10;
          
          for (let i = 1; i <= maxEscala; i++) {
            const val = analise.distribuicao[String(i)] || 0;
            const rowY = chartY + (i - 1) * (chartBarH + 2);
            
            // Label
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 116, 139);
            doc.text(String(i), margin + 5, rowY + 5);
            
            // Barra
            const barColor = i >= 4 ? successColor : i === 3 ? warningColor : dangerColor;
            drawHorizontalBar(margin + 12, rowY, chartBarW, chartBarH, val, maxVal, barColor);
            
            // Contagem
            doc.setTextColor(71, 85, 105);
            doc.text(String(val), margin + 12 + chartBarW + 3, rowY + 5);
          }
          
          // Legenda
          doc.setFontSize(6);
          doc.setTextColor(148, 163, 184);
          if (analise.tipo === 'escala_1_5') {
            doc.text('1=Discordo total | 5=Concordo total', margin + 5, chartY + maxEscala * (chartBarH + 2) + 3);
          }
        }
        
        // Gráfico para Sim/Não
        if (analise.tipo === 'sim_nao' && analise.distribuicao) {
          const chartY = y + 22;
          const simCount = analise.distribuicao['true'] || analise.distribuicao['Sim'] || 0;
          const naoCount = analise.distribuicao['false'] || analise.distribuicao['Não'] || 0;
          const total = simCount + naoCount;
          const simPct = total > 0 ? (simCount / total * 100).toFixed(0) : 0;
          const naoPct = total > 0 ? (naoCount / total * 100).toFixed(0) : 0;
          
          // Barra Sim
          doc.setFontSize(8);
          doc.text('Sim', margin + 5, chartY + 5);
          drawHorizontalBar(margin + 20, chartY, 80, 8, simCount, total || 1, successColor);
          doc.text(`${simPct}% (${simCount})`, margin + 105, chartY + 5);
          
          // Barra Não
          doc.text('Não', margin + 5, chartY + 15);
          drawHorizontalBar(margin + 20, chartY + 10, 80, 8, naoCount, total || 1, dangerColor);
          doc.text(`${naoPct}% (${naoCount})`, margin + 105, chartY + 15);
        }
        
        // Lista para múltipla escolha
        if (analise.tipo === 'multipla_escolha' && analise.distribuicao) {
          const chartY = y + 22;
          const entries = Object.entries(analise.distribuicao).sort((a, b) => b[1] - a[1]).slice(0, 5);
          const maxVal = entries[0]?.[1] || 1;
          
          entries.forEach(([opcao, count], i) => {
            const rowY = chartY + i * 10;
            doc.setFontSize(7);
            doc.setTextColor(71, 85, 105);
            const opcaoLabel = opcao.length > 25 ? opcao.substring(0, 22) + '...' : opcao;
            doc.text(opcaoLabel, margin + 5, rowY + 5);
            drawHorizontalBar(margin + 55, rowY, 60, 6, count, maxVal, primaryColor);
            doc.text(String(count), margin + 118, rowY + 5);
          });
        }
        
        y += neededHeight;
      });
      
      // ==================== PÁGINA: RANKING DE RESULTADOS ====================
      doc.addPage();
      drawPageHeader('RANKING DE RESULTADOS');
      y = 25;
      
      // Top 5 Pontos Fortes
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(34, 197, 94);
      doc.text('Top 5 Pontos Fortes', margin, y);
      y += 8;
      
      reportData.top_fortes.slice(0, 5).forEach((item, i) => {
        doc.setFillColor(240, 253, 244);
        doc.roundedRect(margin, y, contentWidth, 12, 2, 2, 'F');
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(34, 197, 94);
        doc.text(`${i + 1}º`, margin + 3, y + 8);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);
        const texto = item.texto.length > 70 ? item.texto.substring(0, 67) + '...' : item.texto;
        doc.text(texto, margin + 12, y + 8);
        
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(34, 197, 94);
        doc.text(`${item.percentual}%`, contentWidth - 5, y + 8);
        
        y += 14;
      });
      
      y += 10;
      
      // Top 5 Oportunidades de Melhoria
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(239, 68, 68);
      doc.text('Top 5 Oportunidades de Melhoria', margin, y);
      y += 8;
      
      reportData.top_fracos.slice(0, 5).forEach((item, i) => {
        doc.setFillColor(254, 242, 242);
        doc.roundedRect(margin, y, contentWidth, 12, 2, 2, 'F');
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(239, 68, 68);
        doc.text(`${i + 1}º`, margin + 3, y + 8);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);
        const texto = item.texto.length > 70 ? item.texto.substring(0, 67) + '...' : item.texto;
        doc.text(texto, margin + 12, y + 8);
        
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(239, 68, 68);
        doc.text(`${item.percentual}%`, contentWidth - 5, y + 8);
        
        y += 14;
      });
      
      // ==================== PÁGINA: COMPARATIVO HISTÓRICO (se houver) ====================
      if (comparisonData && comparisonData.comparacao_geral.historico.length > 0) {
        doc.addPage();
        drawPageHeader('COMPARATIVO HISTÓRICO');
        y = 25;
        
        // Título
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('Evolução dos Indicadores', margin, y);
        y += 10;
        
        // Tabela de comparação geral
        const colWidths = [70, 30, 30, 30];
        const headers = ['Indicador', 'Atual', 'Anterior', 'Variação'];
        
        // Header da tabela
        doc.setFillColor(...primaryColor);
        doc.rect(margin, y, contentWidth, 8, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        let headerX = margin + 3;
        headers.forEach((h, i) => {
          doc.text(h, headerX, y + 5.5);
          headerX += colWidths[i];
        });
        y += 8;
        
        // Dados da comparação geral (última pesquisa comparada)
        const lastComp = comparisonData.comparacao_geral.historico[comparisonData.comparacao_geral.historico.length - 1];
        const comparacaoLinhas = [
          {
            indicador: 'Índice Geral de Satisfação',
            atual: `${comparisonData.comparacao_geral.atual.indice_geral}%`,
            anterior: `${lastComp.indice_geral}%`,
            variacao: lastComp.variacao_indice,
            tendencia: lastComp.tendencia_indice
          },
          {
            indicador: 'Taxa de Participação',
            atual: `${comparisonData.comparacao_geral.atual.taxa_participacao}%`,
            anterior: `${lastComp.taxa_participacao}%`,
            variacao: lastComp.variacao_participacao,
            tendencia: lastComp.variacao_participacao > 0 ? 'up' : lastComp.variacao_participacao < 0 ? 'down' : 'stable'
          },
          {
            indicador: 'Total de Respostas',
            atual: String(comparisonData.comparacao_geral.atual.total_respostas),
            anterior: String(lastComp.total_respostas),
            variacao: comparisonData.comparacao_geral.atual.total_respostas - lastComp.total_respostas,
            tendencia: comparisonData.comparacao_geral.atual.total_respostas > lastComp.total_respostas ? 'up' : 'down'
          }
        ];
        
        comparacaoLinhas.forEach((linha, i) => {
          const bgColor = i % 2 === 0 ? [249, 250, 251] : [255, 255, 255];
          doc.setFillColor(...bgColor);
          doc.rect(margin, y, contentWidth, 8, 'F');
          
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(30, 41, 59);
          
          let cellX = margin + 3;
          doc.text(linha.indicador, cellX, y + 5.5);
          cellX += colWidths[0];
          doc.text(linha.atual, cellX, y + 5.5);
          cellX += colWidths[1];
          doc.text(linha.anterior, cellX, y + 5.5);
          cellX += colWidths[2];
          
          // Variação com cor
          const varColor = linha.tendencia === 'up' ? successColor : linha.tendencia === 'down' ? dangerColor : [100, 116, 139];
          doc.setTextColor(...varColor);
          doc.setFont('helvetica', 'bold');
          const varText = linha.variacao > 0 ? `+${linha.variacao}` : String(linha.variacao);
          const arrow = linha.tendencia === 'up' ? '▲' : linha.tendencia === 'down' ? '▼' : '—';
          doc.text(`${arrow} ${varText}`, cellX, y + 5.5);
          
          y += 8;
        });
        
        y += 15;
        
        // Comparação por Categoria
        if (comparisonData.comparacao_categorias.length > 0) {
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 41, 59);
          doc.text('Evolução por Categoria', margin, y);
          y += 10;
          
          comparisonData.comparacao_categorias.forEach((cat) => {
            y = checkNewPage(y, 25);
            
            const lastHistorico = cat.historico[cat.historico.length - 1];
            if (!lastHistorico) return;
            
            // Card da categoria
            doc.setFillColor(249, 250, 251);
            doc.roundedRect(margin, y, contentWidth, 20, 2, 2, 'F');
            
            // Nome
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text(cat.categoria, margin + 5, y + 7);
            
            // Valores atuais e anteriores
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 116, 139);
            doc.text(`Atual: ${cat.atual}%`, margin + 5, y + 14);
            doc.text(`Anterior: ${lastHistorico.percentual}%`, margin + 45, y + 14);
            
            // Variação visual
            const varColor = lastHistorico.tendencia === 'up' ? successColor : 
                            lastHistorico.tendencia === 'down' ? dangerColor : [100, 116, 139];
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...varColor);
            const arrow = lastHistorico.tendencia === 'up' ? '▲' : lastHistorico.tendencia === 'down' ? '▼' : '—';
            const varText = lastHistorico.variacao > 0 ? `+${lastHistorico.variacao}%` : `${lastHistorico.variacao}%`;
            doc.text(`${arrow} ${varText}`, contentWidth - 15, y + 10);
            
            // Barra comparativa
            const barY = y + 3;
            const barW = 50;
            drawHorizontalBar(contentWidth - 75, barY, barW, 4, lastHistorico.percentual, 100, [200, 200, 200]);
            drawHorizontalBar(contentWidth - 75, barY + 5, barW, 4, cat.atual, 100, varColor);
            
            y += 22;
          });
        }
        
        y += 10;
        
        // Comparação por Pergunta (se houver)
        if (comparisonData.comparacao_perguntas.length > 0) {
          y = checkNewPage(y, 40);
          if (y === 20) {
            drawPageHeader('COMPARATIVO HISTÓRICO (cont.)');
            y = 25;
          }
          
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 41, 59);
          doc.text(`Evolução por Pergunta (${comparisonData.total_perguntas_compativeis} perguntas comparáveis)`, margin, y);
          y += 10;
          
          comparisonData.comparacao_perguntas.slice(0, 10).forEach((perg, idx) => {
            y = checkNewPage(y, 18);
            
            const lastHistorico = perg.historico[perg.historico.length - 1];
            if (!lastHistorico) return;
            
            doc.setFillColor(idx % 2 === 0 ? 249 : 255, idx % 2 === 0 ? 250 : 255, idx % 2 === 0 ? 251 : 255);
            doc.roundedRect(margin, y, contentWidth, 15, 1, 1, 'F');
            
            // Texto da pergunta
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(30, 41, 59);
            const pergText = perg.texto.length > 60 ? perg.texto.substring(0, 57) + '...' : perg.texto;
            doc.text(pergText, margin + 3, y + 5);
            
            // Valores
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.text(`${perg.atual.percentual}% vs ${lastHistorico.percentual}%`, margin + 3, y + 11);
            
            // Variação
            const varColor = lastHistorico.tendencia === 'up' ? successColor : 
                            lastHistorico.tendencia === 'down' ? dangerColor : [100, 116, 139];
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...varColor);
            const arrow = lastHistorico.tendencia === 'up' ? '▲' : lastHistorico.tendencia === 'down' ? '▼' : '—';
            const varText = lastHistorico.variacao > 0 ? `+${lastHistorico.variacao}` : String(lastHistorico.variacao);
            doc.text(`${arrow} ${varText}%`, contentWidth - 15, y + 8);
            
            y += 16;
          });
          
          if (comparisonData.comparacao_perguntas.length > 10) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(148, 163, 184);
            doc.text(`+ ${comparisonData.comparacao_perguntas.length - 10} perguntas adicionais não listadas`, margin, y + 5);
          }
        }
      }
      
      // ==================== PÁGINA: ANÁLISE POR DEPARTAMENTO ====================
      if (Object.keys(reportData.analise_departamentos).length > 0) {
        doc.addPage();
        drawPageHeader('ANÁLISE POR DEPARTAMENTO');
        y = 25;
        
        // Tabela de departamentos
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('Comparativo por Área', margin, y);
        y += 10;
        
        // Header da tabela
        doc.setFillColor(...primaryColor);
        doc.rect(margin, y, contentWidth, 8, 'F');
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.text('Departamento', margin + 3, y + 5.5);
        doc.text('Respostas', contentWidth - 55, y + 5.5);
        doc.text('Média Geral', contentWidth - 20, y + 5.5);
        y += 8;
        
        // Linhas da tabela
        const deptsSorted = Object.entries(reportData.analise_departamentos)
          .sort((a, b) => b[1].media_geral - a[1].media_geral);
        
        deptsSorted.forEach(([dept, data], i) => {
          const bgColor = i % 2 === 0 ? [249, 250, 251] : [255, 255, 255];
          doc.setFillColor(...bgColor);
          doc.rect(margin, y, contentWidth, 8, 'F');
          
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(30, 41, 59);
          doc.text(dept.substring(0, 35), margin + 3, y + 5.5);
          doc.text(data.total.toString(), contentWidth - 50, y + 5.5);
          
          let scoreColor = successColor;
          if (data.media_geral < 40) scoreColor = dangerColor;
          else if (data.media_geral < 60) scoreColor = warningColor;
          
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...scoreColor);
          doc.text(`${data.media_geral}%`, contentWidth - 15, y + 5.5);
          
          y += 8;
        });
      }
      
      // ==================== RODAPÉS ====================
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        if (i > 1) {
          doc.text(reportData.empresa.nome || 'G2eGente', margin, pageHeight - 10);
          doc.text('Pesquisa de Clima', pageWidth - margin - 30, pageHeight - 10);
        }
      }
      
      // Salvar PDF
      const nomeArquivo = `Relatorio_Clima_${reportData.pesquisa.titulo.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      doc.save(nomeArquivo);
      
      toast.success('Relatório PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar relatório');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const getScoreColor = (score, max = 5) => {
    const percent = (score / max) * 100;
    if (percent >= 80) return 'text-emerald-600';
    if (percent >= 60) return 'text-yellow-600';
    if (percent >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreIcon = (score, max = 5) => {
    const percent = (score / max) * 100;
    if (percent >= 70) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (percent >= 50) return <Minus className="w-4 h-4 text-yellow-500" />;
    return <TrendingDown className="w-4 h-4 text-red-500" />;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (!results) return null;

  const { survey, total_respostas, total_elegivel, taxa_participacao, pode_exibir_graficos, perguntas_analise, analise_departamentos } = results;

  // Preparar dados para gráfico radar (média geral de perguntas escala)
  const radarData = perguntas_analise
    .filter(p => p.tipo === 'escala_1_5' && p.media)
    .map((p, i) => ({
      pergunta: `P${i + 1}`,
      media: p.media,
      fullText: p.texto
    }));

  // Preparar dados por departamento
  const deptData = Object.entries(analise_departamentos || {}).map(([dept, data]) => ({
    departamento: dept,
    respostas: data.total,
    ...data.medias
  }));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/company/climate-surveys')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-cyan-600" />
            Resultados da Pesquisa
          </h1>
          <p className="text-slate-500">{survey.titulo}</p>
        </div>
        <Badge className={survey.status === 'ativa' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
          {survey.status === 'ativa' ? 'Ativa' : 'Encerrada'}
        </Badge>
        {pode_exibir_graficos && (
          <div className="flex items-center gap-2">
            {/* Botão de comparação */}
            {availableComparisons.length > 0 && (
              <div className="relative">
                <Button 
                  variant="outline"
                  onClick={() => setShowComparisonSelector(!showComparisonSelector)}
                  className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  data-testid="comparison-selector-btn"
                >
                  <GitCompare className="w-4 h-4 mr-2" />
                  Comparar
                  {selectedComparisons.length > 0 && (
                    <Badge className="ml-2 bg-cyan-600 text-white text-xs">{selectedComparisons.length}</Badge>
                  )}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
                
                {showComparisonSelector && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white border rounded-lg shadow-lg z-50 p-3">
                    <div className="text-sm font-medium text-slate-700 mb-2">
                      Selecione pesquisas para comparar:
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {availableComparisons.map((comp) => (
                        <div 
                          key={comp.id}
                          onClick={() => toggleComparison(comp.id)}
                          className={`p-2 rounded cursor-pointer border transition-colors ${
                            selectedComparisons.includes(comp.id) 
                              ? 'bg-cyan-50 border-cyan-300' 
                              : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                          }`}
                          data-testid={`comparison-option-${comp.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{comp.titulo}</p>
                              <p className="text-xs text-slate-500">
                                {comp.data_fim 
                                  ? new Date(comp.data_fim).toLocaleDateString('pt-BR') 
                                  : comp.data_inicio 
                                    ? new Date(comp.data_inicio).toLocaleDateString('pt-BR')
                                    : 'Sem data'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-right">
                                <span className="text-cyan-600">{comp.perguntas_compativeis}</span>
                                <span className="text-slate-400"> perg.</span>
                                <span className="text-slate-300 mx-1">|</span>
                                <span className="text-emerald-600">{comp.categorias_compativeis}</span>
                                <span className="text-slate-400"> cat.</span>
                              </div>
                              {selectedComparisons.includes(comp.id) && (
                                <Check className="w-4 h-4 text-cyan-600" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedComparisons.length > 0 && (
                      <div className="mt-3 pt-3 border-t flex justify-between items-center">
                        <span className="text-xs text-slate-500">
                          {selectedComparisons.length} selecionada(s)
                        </span>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => setSelectedComparisons([])}
                          className="text-xs h-7"
                        >
                          Limpar
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Botão PDF */}
            <Button 
              onClick={handleGeneratePDF}
              disabled={generatingPDF}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="generate-pdf-btn"
            >
              {generatingPDF ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              {selectedComparisons.length > 0 ? 'PDF Comparativo' : 'Relatório PDF'}
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{total_respostas}</p>
                <p className="text-sm text-slate-500">Respostas recebidas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Percent className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{taxa_participacao}%</p>
                <p className="text-sm text-slate-500">Taxa de participação</p>
              </div>
            </div>
            <Progress value={taxa_participacao} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <ThermometerSun className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{total_elegivel}</p>
                <p className="text-sm text-slate-500">Funcionários elegíveis</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Not enough responses warning */}
      {!pode_exibir_graficos && (
        <Card className="mb-6 bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              <div>
                <h4 className="font-semibold text-amber-800">Aguardando mais respostas</h4>
                <p className="text-sm text-amber-700">
                  Os gráficos serão exibidos quando houver pelo menos 2 respostas. 
                  Atualmente há {total_respostas} resposta(s).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {pode_exibir_graficos && (
        <>
          {/* Radar Chart - Overview */}
          {radarData.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Visão Geral - Médias por Pergunta</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="pergunta" />
                      <PolarRadiusAxis domain={[0, 5]} />
                      <Radar
                        name="Média"
                        dataKey="media"
                        stroke="#06b6d4"
                        fill="#06b6d4"
                        fillOpacity={0.5}
                      />
                      <Tooltip 
                        formatter={(value) => [value.toFixed(2), 'Média']}
                        labelFormatter={(label) => {
                          const item = radarData.find(d => d.pergunta === label);
                          return item?.fullText || label;
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per Question Analysis */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Análise por Pergunta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {perguntas_analise.map((analise, index) => (
                <div key={analise.pergunta_id} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {analise.tipo === 'escala_1_5' ? 'Escala 1-5' : 
                           analise.tipo === 'escala_1_10' ? 'Escala 1-10' :
                           analise.tipo === 'sim_nao' ? 'Sim/Não' :
                           analise.tipo === 'texto' ? 'Texto' : 'Múltipla'}
                        </Badge>
                        <span className="text-sm text-slate-500">
                          {analise.total_respostas} respostas
                        </span>
                      </div>
                      <p className="font-medium text-slate-800">
                        {index + 1}. {analise.texto}
                      </p>
                    </div>
                    {analise.media && (
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getScoreColor(analise.media)}`}>
                          {analise.media.toFixed(1)}
                        </div>
                        <div className="flex items-center justify-end gap-1 text-sm text-slate-500">
                          {getScoreIcon(analise.media)}
                          de 5
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Distribution Chart for Scale Questions */}
                  {(analise.tipo === 'escala_1_5' || analise.tipo === 'escala_1_10') && analise.distribuicao && (
                    <div className="h-[150px] mt-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={Object.entries(analise.distribuicao).map(([key, value]) => ({
                          nota: key,
                          quantidade: value,
                          label: SCALE_LABELS[key] || key
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="nota" />
                          <YAxis allowDecimals={false} />
                          <Tooltip 
                            formatter={(value, name, props) => [value, 'Respostas']}
                            labelFormatter={(label) => SCALE_LABELS[label] || `Nota ${label}`}
                          />
                          <Bar dataKey="quantidade" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Pie Chart for Yes/No or Multiple Choice */}
                  {(analise.tipo === 'sim_nao' || analise.tipo === 'multipla_escolha') && analise.distribuicao && (
                    <div className="h-[200px] mt-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={Object.entries(analise.distribuicao).map(([key, value]) => ({
                              name: key === 'true' ? 'Sim' : key === 'false' ? 'Não' : key,
                              value
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {Object.entries(analise.distribuicao).map((entry, i) => (
                              <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Text Responses */}
                  {analise.tipo === 'texto' && analise.respostas_texto?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                        <MessageSquare className="w-4 h-4" />
                        Respostas anônimas:
                      </div>
                      <div className="max-h-[200px] overflow-y-auto space-y-2">
                        {analise.respostas_texto.map((texto, i) => (
                          <div key={i} className="p-3 bg-white rounded border border-slate-200 text-sm text-slate-700 italic">
                            {texto}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Department Analysis */}
          {Object.keys(analise_departamentos || {}).length > 1 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building className="w-5 h-5 text-cyan-600" />
                  Análise por Departamento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(analise_departamentos).map(([dept, data]) => {
                    const avgScores = Object.values(data.medias || {});
                    const overallAvg = avgScores.length > 0 
                      ? (avgScores.reduce((a, b) => a + b, 0) / avgScores.length).toFixed(2)
                      : null;

                    return (
                      <div key={dept} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-800">{dept}</h4>
                            <p className="text-sm text-slate-500">{data.total} respostas</p>
                          </div>
                          {overallAvg && (
                            <div className={`text-xl font-bold ${getScoreColor(parseFloat(overallAvg))}`}>
                              {overallAvg}
                              <span className="text-sm text-slate-400 font-normal ml-1">média</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* LGPD Notice */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
              <ThermometerSun className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-700 mb-1">Confidencialidade Garantida</h4>
              <p className="text-sm text-slate-600">
                Todas as respostas são anônimas. Não é possível identificar individualmente 
                quem respondeu a pesquisa. Os dados são apresentados de forma agregada 
                em conformidade com a LGPD.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
